import React, { useEffect, useMemo } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

const TITLES = {
  requestOpen: 'Demander l’ouverture de ma caisse',
  requestApport: 'Demander un apport',
  requestRetrait: 'Demander un retrait',
  requestClose: 'Demander la clôture',
  directOpen: 'Ouvrir directement la caisse',
  directApport: 'Faire un apport direct',
  directRetrait: 'Faire un retrait direct',
  directClose: 'Clôturer directement la caisse',
};

export default function CashSessionActionModal({
  show,
  mode,
  session,
  previousBalance = null,
  canEditOpeningAmounts = false,
  onHide,
  onSubmit,
}) {
  const previousUsd = Number(previousBalance?.usd || 0);
  const previousHtg = Number(previousBalance?.htg || 0);

  const defaultValues = useMemo(() => ({
    openUsd: previousUsd.toFixed(2),
    openHtg: previousHtg.toFixed(2),
    currency: 'HTG',
    amount: '',
    realBalanceUsd: session?.systemBalanceUsd ?? '',
    realBalanceHtg: session?.systemBalanceHtg ?? '',
    note: '',
  }), [previousUsd, previousHtg, session]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues });

  useEffect(() => {
    if (show) reset(defaultValues);
  }, [show, defaultValues, reset]);

  const isOpenMode = mode === 'requestOpen' || mode === 'directOpen';
  const isApportRetrait = ['requestApport', 'requestRetrait', 'directApport', 'directRetrait'].includes(mode);
  const isCloseMode = mode === 'requestClose' || mode === 'directClose';
  const isUserClose = mode === 'requestClose';

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{TITLES[mode] || 'Action caisse'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {isOpenMode && (
            <>
              <Alert variant="light" className="border mb-3">
                <Row className="g-2">
                  <Col md={6}>
                    <div className="text-muted small">Solde antérieur réel USD</div>
                    <div className="fw-semibold">{previousUsd.toFixed(2)} USD</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted small">Solde antérieur réel HTG</div>
                    <div className="fw-semibold">{previousHtg.toFixed(2)} HTG</div>
                  </Col>
                </Row>
              </Alert>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Montant d’ouverture USD</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    readOnly={!canEditOpeningAmounts}
                    {...register('openUsd')}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Montant d’ouverture HTG</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    readOnly={!canEditOpeningAmounts}
                    {...register('openHtg')}
                  />
                </Col>
              </Row>

              <div className="small text-muted mt-2">
                {canEditOpeningAmounts
                  ? 'Les montants sont préremplis avec les derniers soldes réels clôturés et peuvent être modifiés.'
                  : 'Les montants sont automatiquement repris des derniers soldes réels clôturés.'}
              </div>
            </>
          )}

          {isApportRetrait && (
            <Row className="g-3">
              <Col md={5}>
                <Form.Label>Devise</Form.Label>
                <Form.Select {...register('currency')}>
                  <option value="HTG">HTG</option>
                  <option value="USD">USD</option>
                </Form.Select>
              </Col>
              <Col md={7}>
                <Form.Label>Montant</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('amount', {
                    required: 'Requis',
                    validate: (v) => Number(v) > 0 || 'Doit être > 0',
                  })}
                />
                {errors.amount && <Form.Text className="text-danger">{errors.amount.message}</Form.Text>}
              </Col>
            </Row>
          )}

          {isCloseMode && (
            <>
              <Alert variant={isUserClose ? 'warning' : 'info'} className="mb-3">
                {isUserClose
                  ? 'Avant d’envoyer la demande, renseigne le solde réel constaté physiquement. Le système calculera l’écart côté utilisateur.'
                  : 'Renseigne le solde réel constaté par l’administrateur. Le système calculera l’écart administratif.'}
              </Alert>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>{isUserClose ? 'Solde réel utilisateur USD' : 'Solde réel admin USD'}</Form.Label>
                  <Form.Control type="number" step="0.01" {...register('realBalanceUsd')} />
                </Col>
                <Col md={6}>
                  <Form.Label>{isUserClose ? 'Solde réel utilisateur HTG' : 'Solde réel admin HTG'}</Form.Label>
                  <Form.Control type="number" step="0.01" {...register('realBalanceHtg')} />
                </Col>
              </Row>

              <div className="small text-muted mt-2">
                Solde système actuel: USD {Number(session?.systemBalanceUsd || 0).toFixed(2)} / HTG {Number(session?.systemBalanceHtg || 0).toFixed(2)}
              </div>
            </>
          )}

          <Form.Group className="mt-3">
            <Form.Label>Note</Form.Label>
            <Form.Control as="textarea" rows={3} {...register('note')} />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit">
            {mode.startsWith('request') ? 'Envoyer la demande' : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
